import React, { useState, useMemo, useEffect, useRef } from 'react';
import { compose } from 'redux';
import { withProfile } from 'modules/v2/containers';
import { withAuth } from 'modules/auth/containers';
import DashboardTemplate from 'modules/v2/templates/DashboardTemplate';
import { DesignHuddleProvider, useDesignHuddle } from 'modules/v2/pages/TemplatesBuilder/DesignHuddleContext';
import RelativeLoading from 'modules/v2/common/AtomicDesign/RelativeLoading';
import { Divider } from 'antd';
import { Badge } from 'flowbite-react';
import { isEmpty, isEqual, map, uniqWith } from 'lodash';
import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import { PreviewModal } from 'modules/v2/common/components';
import { useHistory } from 'react-router';
import { categoriesList } from 'modules/v2/pages/TemplatesBuilder/utils/template';
import { getInstance as getDSInstance } from 'modules/api/digitalServices/instance';
import ExportProgressModal from '../components/ExportProgressModal';
import ProjectSelectionModal from '../components/ProjectSelectionModal';

const ProjectCard = ({ project, onClick, onCustomize, onPreview }) => {
  const { legacyDhProjects } = useDesignHuddle();

  const getProjectThumbById = () => {
    const foundProject = legacyDhProjects.find((currentProject) => currentProject.project_id.includes(project.projectId));

    return foundProject?.thumbnail_url || null;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden max-w-[290px]">
      <div className="group hover:brightness-[90%] transition duration-300 ease-in-out aspect-[3/4] bg-gray-100 overflow-hidden relative max-h-[384px]">
        {getProjectThumbById() ? (
          <img
            src={getProjectThumbById()}
            alt={project.metadata?.project?.project_title || 'Project'}
            className="h-full w-full object-contain group-hover:scale-110 transition duration-300 ease-in-out"
            style={{ imageRendering: 'crisp-edges' }}
            onError={(e) => {
              const target = e.target;
              target.style.display = 'none';
              const nextElement = target.nextElementSibling;
              if (nextElement) {
                nextElement.style.display = 'flex';
              }
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-400">
            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
        )}
        {project.categoryId && (
          <div className="absolute top-[5%] right-[5%]">
            <Badge className="text-orange-600 bg-orange-50 group-hover:bg-orange-50 text-sm leading-5 font-semibold">
              {getCategoryName(project.categoryId)}
            </Badge>
          </div>
        )}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="flex flex-col w-[90%] gap-2">
            <Button
              className="min-w-[100%]"
              onClick={(e) => {
                e.stopPropagation();
                onCustomize(project);
              }}
            >
              <span className="invisible group-hover:visible ">Customize</span>
            </Button>
            <Button
              className="min-w-[100%]"
              onClick={(e) => {
                e.stopPropagation();
                onPreview(project);
              }}
              type="outlined"
            >
              <span className="flex gap-1 invisible group-hover:visible">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Preview
              </span>
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border-t border-gray-200 text-sm leading-5 font-semibold p-4 pt-2">
        {project?.metadata?.project?.project_title}
      </div>
    </div>
  );
};

const getCategoryName = (categoryId) => {
  const category = categoriesList.find(cat => cat.id === categoryId);
  return category ? category.name : 'General';
};

const PROJECT_TYPES = [
  { id: 0, name: 'All' },
  ...categoriesList
];

const MyProjectsContent = () => {
  const {
    dhProjects,
    isLoadingProjects,
    legacyDhProjects,
    userProfile,
  } = useDesignHuddle();

  const DSInstance = getDSInstance();
  const getProjectThumbById = (id) => {
    return legacyDhProjects.find((project) => project.project_id === id)?.thumbnail_url || '';
  };

  const history = useHistory();
  const [selectedType, setSelectedType] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewProject, setPreviewProject] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [exportJobs, setExportJobs] = useState([]);
  const [isStartingExport, setIsStartingExport] = useState(false);
  const categoryContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Check scroll position and overflow
  const checkScrollPosition = () => {
    if (categoryContainerRef.current) {
      const container = categoryContainerRef.current;
      const isOverflowing = container.scrollWidth > container.clientWidth;
      const isAtStart = container.scrollLeft <= 5;
      const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 5;

      setShowLeftArrow(isOverflowing && !isAtStart);
      setShowRightArrow(isOverflowing && !isAtEnd);
    }
  };

  useEffect(() => {
    checkScrollPosition();
    window.addEventListener('resize', checkScrollPosition);
    return () => window.removeEventListener('resize', checkScrollPosition);
  }, []);

  const scrollCategories = (direction) => {
    if (categoryContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = direction === 'left'
        ? categoryContainerRef.current.scrollLeft - scrollAmount
        : categoryContainerRef.current.scrollLeft + scrollAmount;

      categoryContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const filteredProjects = useMemo(() => {
    if (!dhProjects || dhProjects.length === 0) return [];

    console.log('dhProjects', dhProjects);

    return dhProjects.filter(project => {
      const matchesSearch = !searchQuery ||
        project.metadata?.project?.project_title?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = selectedType === 0 ||
        project.categoryId === selectedType;

      return matchesSearch && matchesType;
    });
  }, [dhProjects, searchQuery, selectedType]);

  const handleProjectClick = (project) => {
    history.push(`/templates/project/${project.projectId}`);
  };

  const handlePreview = (project) => {
    if (project.metadata?.project?.thumbnail_url) {
      setPreviewProject(project);
      setShowPreviewModal(true);
    }
  };

  const socialMediaProjects = useMemo(() => {
    return dhProjects.filter(project => project.categoryId === 4);
  }, [dhProjects]);

  const handleOpenDownloadModal = () => {
    setShowDownloadModal(true);
  };

  const handleToggleProject = (projectId) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      }
      return [...prev, projectId];
    });
  };

  const handleToggleAll = () => {
    if (selectedProjects.length === socialMediaProjects.length) {
      setSelectedProjects([]);
    } else {
      setSelectedProjects(socialMediaProjects.map(p => p.projectId));
    }
  };

  const handleDownload = async () => {
    try {
      setIsStartingExport(true);
      const response = await DSInstance.post('/template-builder/projects/export', {
        projectIds: selectedProjects,
      });

      if (response.data && response.data.jobs) {
        setExportJobs(response.data.jobs);
        setShowDownloadModal(false);
        setSelectedProjects([]);
        setShowProgressModal(true);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error starting export:', error);
      alert('Error starting export. Please try again.');
    } finally {
      setIsStartingExport(false);
    }
  };


  if (isLoadingProjects) {
    return <RelativeLoading isLoading={true} />;
  }

  return (
    <div className="w-full">
      {/* <div className="mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 transition-shadow overflow-hidden p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">My Projects</h2>
          <p className="text-gray-600 mb-6">
            Manage and edit your custom projects created from templates. Access your designs anytime and continue where you left off.
          </p>

          <div className="space-y-3">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <div>
                <span className="font-semibold">Quick Access.</span> All your projects in one place for easy management.
              </div>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <div>
                <span className="font-semibold">Continue Editing.</span> Pick up right where you left off with auto-saved progress.
              </div>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <div>
                <span className="font-semibold">Organized by Type.</span> Filter projects by category to find what you need faster.
              </div>
            </div>
          </div>
        </div>
      </div> */}

      <div className="border-b border-gray-200 mb-6 relative">
        {/* Left Arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scrollCategories('left')}
            className="absolute left-0 top-0 z-10 h-12 w-10 bg-gradient-to-r from-white to-transparent flex items-center justify-start hover:from-gray-50 transition-colors"
            aria-label="Scroll left"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Categories Container */}
        <div
          ref={categoryContainerRef}
          className="overflow-x-auto overflow-y-hidden scrollbar-hide"
          onScroll={checkScrollPosition}
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <nav className="-mb-px flex space-x-8" style={{ minWidth: 'max-content' }}>
            {PROJECT_TYPES.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedType(category.id)}
                className={`py-2 px-1 border-b-2 font-semibold text-sm whitespace-nowrap ${
                  selectedType === category.id
                    ? 'border-cyan-500 text-neutral-800'
                    : 'border-transparent text-neutral-600 hover:text-neutral-700 hover:border-gray-300'
                }`}
              >
                {category.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Right Arrow */}
        {showRightArrow && (
          <button
            onClick={() => scrollCategories('right')}
            className="absolute right-0 top-0 z-10 h-12 w-10 bg-gradient-to-l from-white to-transparent flex items-center justify-end hover:from-gray-50 transition-colors"
            aria-label="Scroll right"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        <style jsx>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>

      <div className="bg-white p-6 rounded-md shadow-md overflow-hidden">
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search projects"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>

          <Button
            onClick={handleOpenDownloadModal}
            className="whitespace-nowrap"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Projects
          </Button>
        </div>

        <Divider />

        {filteredProjects.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.projectId}
                project={project}
                onClick={handleProjectClick}
                onCustomize={handleProjectClick}
                onPreview={handlePreview}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-2">No projects found</div>
            <div className="text-gray-500 text-sm">
              {dhProjects?.length === 0
                ? "You haven't created any projects yet. Start by customizing a template!"
                : "Try adjusting your search or filter criteria"
              }
            </div>
            {dhProjects?.length === 0 && (
              <Button
                className="mt-4"
                onClick={() => history.push('/')}
              >
                Browse Templates
              </Button>
            )}
          </div>
        )}
      </div>

      <ProjectSelectionModal
        isOpen={showDownloadModal}
        onClose={() => {
          setShowDownloadModal(false);
          setSelectedProjects([]);
        }}
        projects={socialMediaProjects}
        selectedProjects={selectedProjects}
        onToggleProject={handleToggleProject}
        onToggleAll={handleToggleAll}
        onDownload={handleDownload}
        isStartingExport={isStartingExport}
        getProjectThumbById={getProjectThumbById}
      />

      <ExportProgressModal
        isOpen={showProgressModal}
        onClose={() => {
          setShowProgressModal(false);
          setExportJobs([]);
        }}
        initialJobs={exportJobs}
      />

      <PreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        template={previewProject}
        onCustomize={handleProjectClick}
      />
    </div>
  );
};

const MyProjects = (props) => {
  return (
    <DesignHuddleProvider>
      <DashboardTemplate title="My Projects" centralized {...props}>
        <MyProjectsContent />
      </DashboardTemplate>
    </DesignHuddleProvider>
  );
};

export default compose(withProfile, withAuth)(MyProjects);